/**
 * Zustand store — local player identity.
 *
 * The player ID is a UUID generated on first load and persisted to
 * localStorage so it survives page refreshes.  The server issues a
 * short-lived session token per room which is stored here too.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { v4 as uuidv4 } from 'uuid';

export interface PlayerStore {
  /** Stable UUID identifying this browser / device. */
  playerId: string;
  /** Display name chosen by the player (may be empty until set). */
  displayName: string;
  /** Emoji avatar chosen by the player. */
  avatar: string;
  /** Social-casino profile picture URL from the app launch (null when not provided). */
  avatarUrl: string | null;
  /** Server-issued session token for the current room. Null when not in a room. */
  token: string | null;
  /** Current credit balance fetched from the server. Not persisted (refetched each session). */
  credits: number;
  /** Game IDs the user has marked as favorites. Persisted to localStorage. */
  favorites: string[];

  setPlayerId(id: string): void;
  setDisplayName(name: string): void;
  setAvatar(emoji: string): void;
  setAvatarUrl(url: string | null): void;
  setToken(token: string | null): void;
  setCredits(credits: number): void;
  toggleFavorite(gameId: string): void;
}

export const usePlayerStore = create<PlayerStore>()(
  persist(
    (set) => ({
      // Generate a UUID on first use; subsequent loads restore from localStorage.
      playerId: uuidv4(),
      displayName: '',
      avatar: '🎮',
      avatarUrl: null,
      token: null,
      credits: 0,
      favorites: [],

      setPlayerId(id) {
        set({ playerId: id });
      },

      setDisplayName(name) {
        set({ displayName: name.trim() });
      },

      setAvatar(emoji) {
        set({ avatar: emoji });
      },

      setAvatarUrl(url) {
        set({ avatarUrl: url });
      },

      setToken(token) {
        set({ token });
      },

      setCredits(credits) {
        set({ credits });
      },

      toggleFavorite(gameId) {
        set((s) => ({
          favorites: s.favorites.includes(gameId)
            ? s.favorites.filter((id) => id !== gameId)
            : [...s.favorites, gameId],
        }));
      },
    }),
    {
      name: 'playground-player',
      // Only persist identity fields — not the ephemeral token.
      partialize: (state) => ({
        playerId: state.playerId,
        displayName: state.displayName,
        avatar: state.avatar,
        avatarUrl: state.avatarUrl,
        favorites: state.favorites,
      }),
    },
  ),
);
